import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { logger } from '@/lib/logger'
import { isChunkLoadError, recoverChunkLoadError } from '@/lib/chunk-recovery'

const isNorwegian = typeof navigator !== 'undefined' && /^(nb|nn|no)\b/i.test(navigator.language)

const text = {
  title: isNorwegian ? 'Noe gikk galt' : 'Something went wrong',
  body: isNorwegian
    ? 'En uventet feil oppstod. Prøv å laste siden på nytt.'
    : 'An unexpected error occurred. Try reloading the page.',
  retry: isNorwegian ? 'Prøv igjen' : 'Try again',
  reload: isNorwegian ? 'Last inn på nytt' : 'Reload',
}

interface Props {
  children: ReactNode
  /** Contain an optional surface failure without reloading the current task. */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isStaleChunk: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isStaleChunk: false }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isStaleChunk: isChunkLoadError(error) }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.props.fallback !== undefined) {
      logger.error('Contained surface failed:', error, info.componentStack)
      return
    }
    if (recoverChunkLoadError(error)) return
    logger.error('ErrorBoundary caught:', error, info.componentStack)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback
      return (
        <div className="flex h-full items-center justify-center bg-[var(--surface-page)] px-4">
          <div className="max-w-md w-full text-center">
            <AlertTriangle className="size-14 text-[var(--icon-warning)] mx-auto mb-4" />
            <h1 className="text-h1 text-[var(--text-primary)] mb-2">
              {this.state.isStaleChunk
                ? (isNorwegian ? 'Ny versjon tilgjengelig' : 'New version available')
                : text.title}
            </h1>
            <p className="text-body text-[var(--text-secondary)] mb-6">
              {this.state.isStaleChunk
                ? (isNorwegian ? 'Appen er oppdatert. Last inn på nytt for å fortsette.' : 'The app has been updated. Reload to continue.')
                : text.body}
            </p>
            {this.state.error && (
              <pre
                className="text-left text-small text-[var(--text-muted)] bg-[var(--surface-sunken)] rounded-lg p-3 mb-6 overflow-auto max-h-32"
              >
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-default)] bg-[var(--surface-default)] text-label text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] transition-colors cursor-pointer"
              >
                <RotateCcw className="size-4" />
                {text.retry}
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--action-primary-bg)] text-label text-[var(--action-primary-text)] hover:bg-[var(--action-primary-bg-hover)] transition-colors cursor-pointer"
              >
                {text.reload}
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
